/**
 * Coordinators API: wraps the two privileged Edge Functions
 * (invite-coordinator, manage-coordinator). Every call here is admin-
 * only in practice — both functions independently verify that
 * server-side, using the caller's own JWT, so even a modified frontend
 * cannot bypass the check by calling these functions directly.
 *
 * These are the ONLY places in the entire frontend that ever touch
 * account creation/removal/banning — everything else about a user
 * (their role, their full_name) still goes through the normal
 * `profiles` table via Supabase's regular client, same as before.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';

/**
 * supabase-js's functions.invoke() does NOT parse a non-2xx response
 * body into the `data` return value — `data` is null on error, and
 * `error` is a generic FunctionsHttpError whose own `.message` is a
 * generic string ("Edge Function returned a non-2xx status code"), not
 * the function's actual JSON error message. The real message has to be
 * read from `error.context`, which is the raw Response object — this
 * helper does that, falling back to the generic message only if the
 * body can't be parsed for some reason (network blip, etc.).
 */
async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    try {
      const body = await error.context.json();
      if (body && typeof body.error === 'string') {
        return body.error;
      }
    } catch {
      // fall through to the generic message below
    }
  }
  return error instanceof Error ? error.message : 'Something went wrong. Please try again.';
}

export interface CoordinatorListEntry {
  id: string;
  fullName: string;
  role: 'admin' | 'coordinator';
  createdAt: string;
  email: string | null;
  isDisabled: boolean;
}

export function useCoordinatorsList() {
  return useQuery({
    queryKey: ['coordinators-list'],
    queryFn: async (): Promise<CoordinatorListEntry[]> => {
      const { data, error } = await supabase.functions.invoke('list-coordinators', {
        method: 'POST',
        body: {},
      });

      if (error) {
        throw new Error(await extractFunctionErrorMessage(error));
      }

      return (data as { users: CoordinatorListEntry[] }).users;
    },
  });
}

export interface InviteCoordinatorInput {
  email: string;
  fullName: string;
}

export function useInviteCoordinator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: InviteCoordinatorInput): Promise<{ userId: string | null }> => {
      const { data, error } = await supabase.functions.invoke('invite-coordinator', {
        body: input,
      });

      if (error) {
        throw new Error(await extractFunctionErrorMessage(error));
      }

      return data as { userId: string | null };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coordinators-list'] });
    },
  });
}

export type CoordinatorAction = 'disable' | 'enable' | 'remove';

export function useManageCoordinator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { userId: string; action: CoordinatorAction }): Promise<void> => {
      const { error } = await supabase.functions.invoke('manage-coordinator', {
        body: input,
      });

      if (error) {
        throw new Error(await extractFunctionErrorMessage(error));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coordinators-list'] });
    },
  });
}

/**
 * Promote/demote a user between coordinator and admin. This does NOT
 * need an Edge Function — unlike invite/disable/remove, changing
 * `role` never touches auth.users, and the existing RLS policy on
 * `profiles` (admin-only writes, see migration 0003) already governs
 * this correctly through the regular client. The
 * prevent_self_role_escalation trigger (also migration 0003) is a
 * second, independent backstop against a non-admin ever granting
 * themselves admin through this same path.
 */
export function useUpdateCoordinatorRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: 'admin' | 'coordinator' }) => {
      const { error } = await supabase.from('profiles').update({ role }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['coordinators-list'] }),
  });
}
